// ActiveFilterBar.tsx
import React from "react";
import { InvoiceFilterFields, InvoiceFilterFieldsPayload } from "../InvoiceFilter/InvoiceFilter.fields";

interface Props {
  activeFilters: InvoiceFilterFieldsPayload | null;
  activeAdvancedSearch: any;
  onRemove: (key: keyof InvoiceFilterFieldsPayload) => void;
  onReset: () => void;
  onAdvancedReset: () => void;
  onAdvancedRemove: (updated: any) => void;
}
const advancedFieldLabels: Record<string, string> = {
  searchTerm: "Search term",
  supplier: "Supplier",
  amountFrom: "Amount from",
  amountTo: "Amount",
  dateFrom: "Date from",
  dateTo: "Date",
  dueDateFrom: "Due date from",
  dueDateTo: "Due date",
  uploadDateFrom: "Upload date from",
  uploadDateTo: "Upload date",
  exportDateFrom: "Export date from",
  exportDateTo: "Export date",
  docRef: "Document reference",
  itemId: "Item ID",
  type: "Type",
  currencyCode: "Currency",
  payment: "Payment",
  user: "User",
  submittedBy: "Submitted by",
  submissionMethod: "Submission method",
  category: "Category",
  name: "Name",
};

export const ActiveFilterBar: React.FC<Props> = ({ activeFilters, activeAdvancedSearch, onRemove, onReset, onAdvancedReset, onAdvancedRemove }) => {
  if (!activeFilters && !activeAdvancedSearch) return null;

  // Build active chips: find label for the selected value
  const activeChips = InvoiceFilterFields.flatMap((field) => {
    const val = activeFilters?.[field.key as keyof InvoiceFilterFieldsPayload];
    if (!val) return [];
    const option = field.options.find((o) => o.value === val);
    return option ? [{ key: field.key, label: option.label }] : [];
  });

  const hasAdvanced = activeAdvancedSearch &&
    Object.values(activeAdvancedSearch).some((v) => v !== null && v !== "");


  if (activeChips.length === 0 && !hasAdvanced) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 14px 15px 15px", flexWrap: "wrap", borderBottom: "1px solid #eee" }}>
      <span style={{ fontSize: "var(--font-size-sm)", color: "#666", fontWeight: 500 }}>Active filter:</span>

      {activeChips.map((chip) => (
        <span key={chip.key}
          onClick={() => onRemove(chip.key as keyof InvoiceFilterFieldsPayload)}
          style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "3px 8px 3px 10px", border: "1.5px solid #e07b2a",
            borderRadius: "999px", background: "#fff8f3",
            fontSize: "var(--font-size-sm)", fontWeight: 500, color: "#a0521a",
            cursor: "pointer",
          }}>
          {chip.label}
          <span
            onClick={() => onRemove(chip.key as keyof InvoiceFilterFieldsPayload)}
            style={{ cursor: "pointer", fontSize: "var(--font-size-base)", lineHeight: 1 }}
          >✕</span>
        </span>
      ))}

      {/* Advanced chip */}
      {hasAdvanced && Object.entries(activeAdvancedSearch)
        .filter(([_, v]) => v !== null && v !== "")
        .map(([key, value]) => {
          const label = advancedFieldLabels[key] || key;
          let displayValue = String(value);

          if (key === "submittedByLabel") return null;
          if (key === "submittedBy" && activeAdvancedSearch.submittedByLabel) {
            displayValue = String(activeAdvancedSearch.submittedByLabel);
          }

          // Skip amountFrom if amountTo exists — shown together
          if (key === "amountFrom" && activeAdvancedSearch["amountTo"]) return null;
          // Skip dateFrom variants if dateTo exists
          if (key === "dateFrom" && activeAdvancedSearch["dateTo"]) return null;
          if (key === "dueDateFrom" && activeAdvancedSearch["dueDateTo"]) return null;
          if (key === "uploadDateFrom" && activeAdvancedSearch["uploadDateTo"]) return null;
          if (key === "exportDateFrom" && activeAdvancedSearch["exportDateTo"]) return null;

          // Combine amount range
          if (key === "amountTo") {
            const from = activeAdvancedSearch["amountFrom"];
            displayValue = from ? `${from} - ${value}` : `Up to ${value}`;
          }
          // Combine date ranges
          const dateToFromMap: Record<string, string> = {
            dateTo: "dateFrom",
            dueDateTo: "dueDateFrom",
            uploadDateTo: "uploadDateFrom",
            exportDateTo: "exportDateFrom",
          };
          if (dateToFromMap[key]) {
            const from = activeAdvancedSearch[dateToFromMap[key]];
            displayValue = from ? `${from} - ${value}` : String(value);
          }

          const handleRemove = () => {
            const updated = { ...activeAdvancedSearch };

            // Clear paired fields together
            if (key === "amountTo" || key === "amountFrom") {
              updated["amountFrom"] = "";
              updated["amountTo"] = "";
            } else if (dateToFromMap[key]) {
              updated[key] = "";
              updated[dateToFromMap[key]] = "";
            } else if (["dateFrom", "dueDateFrom", "uploadDateFrom", "exportDateFrom"].includes(key)) {
              const toKey = key.replace("From", "To");
              updated[key] = "";
              updated[toKey] = "";
            } else {
              updated[key] = "";
              if (key === "submittedBy") {
                updated["submittedByLabel"] = "";
              }
            }

            // Check if any advanced fields remain
            const hasRemaining = Object.values(updated).some(v => v !== null && v !== "");
            if (hasRemaining) {
              onAdvancedRemove(updated);  // refetch with remaining fields
            } else {
              onAdvancedReset();          // all gone — full reset
            }
          };

          return (
            <span
              key={key}
              onClick={handleRemove}
              style={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                padding: "3px 8px 3px 10px", border: "1.5px solid #e07b2a",
                borderRadius: "999px", background: "#fff8f3",
                fontSize: "var(--font-size-sm)", fontWeight: 500, color: "#a0521a",
                cursor: "pointer",
              }}
            >
              {label}: {displayValue}
              <span style={{ cursor: "pointer", fontSize: "var(--font-size-base)", lineHeight: 1 }}>✕</span>
            </span>
          );
        })
      }
      <button onClick={onReset} style={{
        fontSize: "var(--font-size-sm)", color: "#666", background: "none",
        border: "1px solid #ccc", borderRadius: "6px",
        padding: "3px 10px", cursor: "pointer"
      }}>Reset</button>
    </div>
  );
};